import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Hash, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATS = [
  ['nombres_en_archivo', 'Pacientes en archivo'],
  ['actualizados', 'Códigos corregidos'],
  ['ya_correctos', 'Ya estaban bien'],
  ['reasignados_nuevo_codigo', 'Reasignados (código ocupado)'],
  ['errores', 'Con errores'],
];

export const FixPatientCodesCard = ({ token }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const handleFix = async () => {
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/patients/bulk/fix-codes`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setResult(data);
      toast.success(`${data.actualizados} códigos corregidos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al corregir códigos');
    } finally {
      setLoading(false);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Card className="mt-6" data-testid="fix-codes-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0B3B71]">
          <Hash className="h-5 w-5" />
          Corregir códigos de paciente por nombre
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Suba un Excel con columnas <strong>Nombre</strong> y <strong>Código</strong> (por ejemplo, un lote de evaluaciones).
          Cada paciente encontrado por nombre recibirá el código del archivo. Si ese código lo tenía otro paciente, a ese otro se le asigna uno nuevo.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files[0] || null)}
            className="flex-1 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2"
            data-testid="fix-codes-file-input"
          />
          <Button onClick={handleFix} disabled={!file || loading} className="bg-[#0B3B71] hover:bg-[#0B3B71]/90" data-testid="fix-codes-button">
            <Upload className="h-4 w-4 mr-2" />
            {loading ? 'Corrigiendo...' : 'Corregir códigos'}
          </Button>
        </div>
        {result && (
          <div className="space-y-3" data-testid="fix-codes-result">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {STATS.map(([key, label]) => (
                <div key={key} className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-xl font-semibold text-[#0B3B71]" data-testid={`fix-codes-${key}`}>{result[key]}</p>
                  <p className="text-slate-500">{label}</p>
                </div>
              ))}
            </div>
            {result.error_details.length > 0 && (
              <ul className="max-h-48 overflow-y-auto text-sm text-red-700 bg-red-50 rounded-lg p-3 space-y-1" data-testid="fix-codes-errors">
                {result.error_details.map((err, i) => (
                  <li key={i}><strong>{err.nombre}:</strong> {err.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
